import {
  DateInput,
  DayHeaderContentArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Icon, Tooltip } from "@resource/atlas";
import { atlasChevronDown, atlasClose } from "@resource/atlas/icons";
import clsx from "clsx";
import { TimezoneSelector } from "components/TimezoneSelection/AvailabilityTimezoneSelector";
import contrast from "contrast";
import { DateTime } from "luxon";
import {
  ComponentPropsWithoutRef,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  CalendarEvent,
  PersistableCalendarEvent,
  SELECTION_COLOR,
  SUGGESTION_COLOR,
} from "shared/guide-content/rich-blocks/calendar-utils/types";
import { getFormattedEventTimeRange } from "shared/guide-content/rich-blocks/calendar-utils/utils";
import {
  displayTimezone,
  getCurrentIanaTimezone,
} from "shared/utils/timezones";
import { v4 as uuidv4 } from "uuid";

import calendarStyles from "./availability-calendar.module.scss";

type AvailabilityCalendarProps = {
  overlayEvents?: EventInput[];
  suggestions?: PersistableCalendarEvent[];
  selections?: PersistableCalendarEvent[];
  setSelections?: (selections: PersistableCalendarEvent[]) => void;
  loading?: boolean;
  defaultEventTitle?: string;
  timezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  initialDate?: DateInput;
};

type AvailabilityCalendarViewProps = ComponentPropsWithoutRef<"div"> &
  Omit<AvailabilityCalendarProps, "isOpen">;

const TimezoneContext = createContext<string>(getCurrentIanaTimezone());

function TimezoneProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: string;
}) {
  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

function useTimezone() {
  return useContext(TimezoneContext);
}

// A function that returns the bare minimum data we want
// to store in the db as well as the block state
function getPersistableEvent({
  event,
  defaultEventTitle = "Event",
  timezone = getCurrentIanaTimezone(),
}: {
  event: CalendarEvent;
  defaultEventTitle?: string;
  timezone: string;
}): PersistableCalendarEvent {
  const start = DateTime.fromJSDate(event.start ?? new Date(), {
    zone: timezone,
  })
    .toUTC()
    .toISO();
  const end = DateTime.fromJSDate(event.end ?? new Date(), { zone: timezone })
    .toUTC()
    .toISO();
  return {
    id: event.id ?? uuidv4(),
    title: event.title || defaultEventTitle,
    start,
    end,
    allDay: event.allDay,
  };
}

type EventDetailsProps = ComponentPropsWithoutRef<"div"> & {
  event: CalendarEvent;
  timezone?: string;
};
function EventDetails({ event, className, ...props }: EventDetailsProps) {
  const { title } = event;
  const timezone = useTimezone();

  return (
    <Tooltip
      className={clsx("w-full h-full", className)}
      {...props}
      content={title}
    >
      <p className="text-[0.625rem] leading-3 truncate">
        {getFormattedEventTimeRange(event, timezone)}
      </p>
      <p className="text-[0.688rem] font-semibold leading-4 truncate">
        {title}
      </p>
    </Tooltip>
  );
}

function renderEventContent({
  eventInfo,
  onRemove,
}: {
  eventInfo: EventContentArg;
  onRemove: (eventId: string) => void;
}) {
  const { display, backgroundColor } = eventInfo.event;
  const isBackgroundEvent =
    display === "background" || eventInfo.event.extendedProps.isBackground;
  const textClassName =
    contrast(backgroundColor) === "light" ? "text-blue-900" : "text-white";

  if (isBackgroundEvent) {
    return (
      <EventDetails
        className={clsx("p-2", textClassName)}
        event={eventInfo.event}
      />
    );
  }

  return (
    <div className="relative text-xs h-full w-full p-2">
      <button
        className="absolute right-1 top-[0.375rem]"
        type="button"
        onClick={() => onRemove(eventInfo.event.id)}
      >
        <Icon content={atlasClose} className="w-3 h-3" />
      </button>
      <EventDetails
        className={clsx("pr-[0.625rem]", textClassName)}
        event={eventInfo.event}
      />
    </div>
  );
}

function DayContent({ day }: { day: DayHeaderContentArg }) {
  const timezone = useTimezone();

  return (
    <div className="flex flex-col sm:flex-row sm:gap-1 ">
      {DateTime.fromJSDate(day.date).setZone(timezone).toFormat("EEE")}
      <span className="text-body-sm-heavy">
        {DateTime.fromJSDate(day.date).setZone(timezone).toFormat("dd")}
      </span>
    </div>
  );
}

function HeaderTimezonePicker({
  value = getCurrentIanaTimezone(),
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) {
  const [portaledHeaderTimezonepicker, setPortaledHeaderTimezonepicker] =
    useState<JSX.Element | null>(null);
  const headerButton = useRef<Element | null>();

  const [selectedTimezone, setSelectedTimezone] = useState(value);

  useEffect(() => {
    if (!headerButton.current) {
      headerButton.current = document.querySelector(
        ".fc-headerTimezonePicker-button"
      );
    }

    if (headerButton.current) {
      setPortaledHeaderTimezonepicker(
        createPortal(
          <TimezoneSelector
            onClick={(tz) => {
              setSelectedTimezone(tz);
              onChange(tz);
            }}
            rootProps={{
              value: selectedTimezone ?? undefined,
            }}
            triggerProps={{
              trailingContent: (
                <Icon className="text-[#231F33]" content={atlasChevronDown} />
              ),
              className: "custom-select",
              size: "compact",
              renderContent: () => {
                const timezoneDisplay = displayTimezone(
                  selectedTimezone,
                  "abbreviation"
                );
                // we should never show a blank timezone since displayTimezone above works for all valid iana timezones
                const display = timezoneDisplay ?? "-";

                return (
                  <span className="text-body-md-heavy text-[#231F33]">
                    {display}
                  </span>
                );
              },
            }}
          />,
          headerButton.current
        )
      );
    }
  }, [onChange, selectedTimezone, value]);

  return portaledHeaderTimezonepicker;
}

export function AvailabilityCalendar({
  suggestions = [],
  selections = [],
  overlayEvents = [],
  setSelections,
  defaultEventTitle,
  timezone = getCurrentIanaTimezone(),
  onTimezoneChange,
  initialDate,
  ...props
}: AvailabilityCalendarViewProps) {
  const calendarRef = useRef<FullCalendar | null>(null);
  const processedSuggestedEvents = useMemo(
    (): EventInput[] => [
      ...overlayEvents,
      ...suggestions.map((suggestion) => ({
        ...suggestion,
        start: suggestion.start
          ? DateTime.fromISO(suggestion.start, { zone: "utc" })
              .setZone(timezone)
              .toJSDate()
          : undefined,
        end: suggestion.end
          ? DateTime.fromISO(suggestion.end, { zone: "utc" })
              .setZone(timezone)
              .toJSDate()
          : undefined,
        color: SUGGESTION_COLOR,
        display: "background",
        // For interviewer availabilities we can prefill these from the incoming data
        // Maybe names of the interviewers to show which slot belongs to which interviewer
        title: suggestion?.title ?? "Suggested time",
      })),
    ],
    [suggestions, timezone, overlayEvents]
  );
  const [selectedEvents, setSelectedEvents] =
    useState<PersistableCalendarEvent[]>(selections);

  const processedSelectedEvents = useMemo(
    (): EventInput[] =>
      selectedEvents.map((event) => ({
        ...event,
        start: event.start
          ? DateTime.fromISO(event.start, { zone: "utc" })
              .setZone(timezone)
              .toJSDate()
          : undefined,
        end: event.end
          ? DateTime.fromISO(event.end, { zone: "utc" })
              .setZone(timezone)
              .toJSDate()
          : undefined,
        color: SELECTION_COLOR,
        editable: true,
      })),
    [selectedEvents, timezone]
  );
  const aggregateEvents = useMemo(
    () => [...processedSuggestedEvents, ...processedSelectedEvents],
    [processedSuggestedEvents, processedSelectedEvents]
  );

  const handleChange = useCallback(
    (e: CalendarEvent) => {
      // If this is a new event, always set it to 30 minutes (not the default 15)
      if (!e.id && e.end && e.start) {
        const luxonEnd = DateTime.fromJSDate(e.end);
        const luxonStart = DateTime.fromJSDate(e.start);
        const durationInMinutes = luxonEnd.diff(luxonStart, "minutes").minutes;
        if (durationInMinutes === 15) {
          const newEnd = luxonEnd.plus({ minutes: 15 }).toJSDate();
          e.end = newEnd;
        }
      }

      setSelectedEvents((currentEvents) => [
        ...currentEvents.filter((event) => event.id !== e.id),
        getPersistableEvent({ event: e, defaultEventTitle, timezone }),
      ]);
      calendarRef.current?.getApi().unselect();
    },
    [defaultEventTitle, timezone]
  );

  const handleRemove = useCallback(
    (eventId: string) =>
      setSelectedEvents((currentEvents) =>
        currentEvents.filter((event) => event.id !== eventId)
      ),
    []
  );

  useEffect(() => {
    setSelections?.(selectedEvents);
  }, [setSelections, selectedEvents]);

  return (
    <div
      {...props}
      className={clsx(props.className, calendarStyles.availabilityCalendar)}
    >
      <HeaderTimezonePicker
        value={timezone}
        onChange={(value) => onTimezoneChange?.(value)}
      />
      <TimezoneProvider value={timezone}>
        <FullCalendar
          ref={calendarRef}
          timeZone={timezone}
          customButtons={{
            headerTimezoneCaption: {
              text: `All times shown in (${DateTime.now()
                .setZone(timezone)
                .toFormat("ZZZZ")}) ${DateTime.now()
                .setZone(timezone)
                .toFormat("ZZZZZ")}`,
            },
            headerTimezonePicker: {
              text: "",
            },
          }}
          // Not sure if there is another legitimate way to handle extra text along the title
          // Using a custom button to hack this for now
          headerToolbar={{
            start: "title headerTimezoneCaption",
            center: "text",
            end: "today prev,next headerTimezonePicker",
          }}
          plugins={[luxonPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          height="100%"
          events={aggregateEvents}
          nowIndicator={false}
          selectable
          selectOverlap
          selectMirror
          titleFormat={{ year: "numeric", month: "long", timeZone: "short" }}
          viewClassNames="rounded-md"
          initialDate={initialDate}
          dayHeaderClassNames="text-body-sm text-subtle !py-3"
          slotLabelClassNames="text-body-sm text-subtle !pr-2 !pl-6 !border-r-0"
          slotLaneClassNames="!border-l-0"
          scrollTime="09:00:00"
          slotDuration="00:30:00"
          snapDuration="00:15:00"
          slotLabelInterval={{ hours: 1 }}
          expandRows
          eventOrder="order"
          eventOrderStrict
          eventClassNames="!rounded-[0.188rem]"
          longPressDelay={200}
          dayHeaderContent={(day) => <DayContent day={day} />}
          select={(e) => handleChange(e)}
          eventDrop={(e) => handleChange(e.event)}
          eventResize={(e) => handleChange(e.event)}
          eventContent={(e) =>
            renderEventContent({
              eventInfo: e,
              onRemove: handleRemove,
            })
          }
          allDaySlot={false}
        />
      </TimezoneProvider>
    </div>
  );
}
